import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Save, CreditCard, Phone, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { StorefrontAppearanceFields } from "./StorefrontAppearanceFields";
import { StorefrontPreview, type PreviewProduct } from "./StorefrontPreview";
import type { StorefrontBanner } from "../storefront-banners";
import { Loading } from "@/components/ui/loader";
import { PaymentMethodIcon } from "@/components/PaymentMethodIcon";
import { usePermission } from "@/hooks/usePermission";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import {
	storefrontContentKeys,
	useStorefrontSettings,
	type StorefrontSettingsModel,
} from "../hooks/useStorefrontSettings";

export function StorefrontSettingsFields({
	model,
	editing,
	showPreview = true,
	products = [],
	banners = [],
}: {
	model: StorefrontSettingsModel;
	editing: boolean;
	showPreview?: boolean;
	products?: PreviewProduct[];
	banners?: StorefrontBanner[];
}) {
	const { t } = useTranslation();
	if (model.loading) return <Loading />;
	if (model.error)
		return (
			<div role="alert" className="space-y-3">
				<p>{t("pos.storefront.loadFailed")}</p>
				<Button variant="outline" onClick={model.retry}>
					{t("common.retry")}
				</Button>
			</div>
		);
	const field = (key: (typeof storefrontContentKeys)[number]) => {
		const multiline = ["supportAddress", "aboutText", "faqText", "termsText", "privacyText"].includes(key);
		const props = {
			id: `storefront-${key}`,
			value: model.draft.content[key],
			readOnly: !editing,
			placeholder: t(`company.settings.storefront.${key}Placeholder`),
			onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
				model.setDraft(previous => ({
					...previous,
					content: { ...previous.content, [key]: event.target.value },
				})),
		};
		return (
			<div key={key} className="min-w-0 space-y-2">
				<Label htmlFor={props.id}>{t(`company.settings.storefront.${key}`)}</Label>
				{multiline ? <Textarea {...props} rows={3} /> : <Input {...props} dir="ltr" />}
			</div>
		);
	};
	const fields = (
		<div className="min-w-0 space-y-4">
			<StorefrontAppearanceFields model={model} editing={editing} />
			<Card id="storefront-contact">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Phone className="h-5 w-5 text-info" aria-hidden />
						{t("storefrontDesign.contact")}
					</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-3 sm:grid-cols-2">
					{storefrontContentKeys.filter(key => key.startsWith("support")).map(field)}
				</CardContent>
			</Card>
			<Card id="storefront-payments">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<CreditCard className="h-5 w-5 text-success" aria-hidden />
						{t("storefrontDesign.paymentMethods")}
					</CardTitle>
					<p className="text-sm text-muted-foreground">{t("company.settings.storefront.description")}</p>
				</CardHeader>
				<CardContent>
					{model.paymentError ? (
						<p role="status" className="text-sm text-muted-foreground">
							{t("pos.storefront.paymentsUnavailable")}
						</p>
					) : !model.paymentMethods.length ? (
						<p className="text-sm text-muted-foreground">
							{t("company.settings.storefront.noneAvailable")}
						</p>
					) : (
						<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
							{model.paymentMethods.map(method => (
								<Label
									key={method.id}
									htmlFor={`storefront-pm-${method.id}`}
									className="flex min-h-20 cursor-pointer flex-wrap items-center gap-2 rounded-xl border bg-background p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-secondary"
								>
									{method.iconUrl ? (
										<PaymentMethodIcon iconUrl={method.iconUrl} className="h-6 w-6" />
									) : (
										<CreditCard className="h-5 w-5 text-muted-foreground" aria-hidden />
									)}
									<span className="min-w-0 flex-1 break-words text-sm">
										{method.description || method.code}
									</span>
									<Checkbox
										id={`storefront-pm-${method.id}`}
										disabled={!editing}
										checked={model.draft.paymentMethodIds.includes(method.id)}
										onCheckedChange={checked =>
											model.setDraft(previous => ({
												...previous,
												paymentMethodIds: checked
													? [...previous.paymentMethodIds, method.id]
													: previous.paymentMethodIds.filter(id => id !== method.id),
											}))
										}
									/>
								</Label>
							))}
						</div>
					)}
				</CardContent>
			</Card>
			<Card id="storefront-policies">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<FileText className="h-5 w-5 text-violet" aria-hidden />
						{t("storefrontDesign.policies")}
					</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-3 sm:grid-cols-2">
					{storefrontContentKeys.filter(key => !key.startsWith("support")).map(field)}
				</CardContent>
			</Card>
		</div>
	);
	return showPreview ? (
		<div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
			{fields}
			<aside className="min-w-0 xl:sticky xl:top-4">
				<StorefrontPreview model={model} products={products} banners={banners} />
			</aside>
		</div>
	) : (
		fields
	);
}

/** The POS view can edit storefront settings without requiring permission to edit the POS itself. */
export function StorefrontSettingsEditor({
	posId,
	products = [],
	banners = [],
}: {
	posId: string;
	products?: PreviewProduct[];
	banners?: StorefrontBanner[];
}) {
	const canUpdate = usePermission("storefront.update");
	const canRead = usePermission("company.read");
	const enabled = canUpdate && canRead;
	const model = useStorefrontSettings(enabled, posId);
	const { t } = useTranslation();
	const { toast } = useToast();
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	useUnsavedChangesGuard(editing && (model.dirty || !!model.pendingUploads) && !saving);
	if (!enabled) return null;
	const save = async () => {
		setSaving(true);
		try {
			await model.save();
			setEditing(false);
			toast({ title: t("common.success"), description: t("pos.storefront.saved") });
		} catch {
			toast({ title: t("common.error"), description: t("company.errors.updateFailed"), variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};
	return (
		<section id="online-storefront" className="space-y-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-semibold">{t("company.sections.storefront")}</h2>
				</div>
				{!model.loading && !model.error && (
					<div className="flex gap-2">
						{editing ? (
							<>
								<Button
									variant="outline"
									disabled={saving}
									onClick={() => {
										model.reset();
										setEditing(false);
									}}
								>
									{t("common.cancel")}
								</Button>
								<Button disabled={saving || !model.dirty || !!model.pendingUploads} onClick={save}>
									<Save className="me-2 h-4 w-4" />
									{t(saving ? "common.saving" : "pos.storefront.save")}
								</Button>
							</>
						) : (
							<Button variant="outline" onClick={() => setEditing(true)}>
								<Edit className="me-2 h-4 w-4" />
								{t("pos.storefront.edit")}
							</Button>
						)}
					</div>
				)}
			</div>
			<StorefrontSettingsFields
				model={model}
				editing={editing && !saving}
				products={products}
				banners={banners}
			/>
		</section>
	);
}
